import React from "react";

type BaseButtonProps = React.HTMLProps<HTMLAnchorElement> | React.HTMLProps<HTMLButtonElement>;
type buttonType = "button" | "submit" | "reset" | undefined;
/**
 * Click handler for anchor buttons.
 */
const handleAnchorClick = (
    props: React.ButtonHTMLAttributes<HTMLAnchorElement>,
    event: React.MouseEvent<HTMLAnchorElement>
) => {
    const { onClick } = props;
    event.currentTarget.blur();
    onClick?.(event);
};

/**
 * Key press handler for anchor buttons.
 */
const handleAnchorKeyPress = (
    props: React.ButtonHTMLAttributes<HTMLAnchorElement>,
    event: React.KeyboardEvent<HTMLAnchorElement>
) => {
    const { onKeyPress } = props;
    if (event.key === "Enter") {
        onKeyPress?.(event);
    }
};

/**
 * Click handler for actual buttons.
 */
const handleButtonClick = (
    props: React.ButtonHTMLAttributes<HTMLButtonElement>,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
) => {
    const { disabled, onClick } = props;
    if (disabled) {
        event.preventDefault();
        return;
    }
    event.currentTarget.blur();
    onClick?.(event);
};

/**
 * Anchor
 *
 * One of the outputs from rendering the BaseButton.
 * Renders as an \<a>.
 */
const Anchor = (props: any) => {
    const {
        children,
        href,
        target,
        ...restProps
    } = props;
    /* eslint-disable react/jsx-no-target-blank */
    return (
        <a
            {...restProps as React.HTMLProps<HTMLAnchorElement>}
            href={href}
            onClick={handleAnchorClick.bind(null, props)}
            onKeyPress={handleAnchorKeyPress.bind(null, props)}
            target={target}
            rel={target === "_blank" ? "noopener noreferrer" : undefined}
        >
            {children}
        </a>
    );
    /* eslint-enable react/jsx-no-target-blank */
};

/**
 * Button
 *
 * One of the outputs from rendering the BaseButton when including an href prop.
 * Renders as an \<button>.
 */
const Button = (props: React.HTMLProps<HTMLButtonElement>) => {
    const {
        children,
        type = "button",
        ...restProps
    } = props;
    return (
        <button
            {...restProps as React.ButtonHTMLAttributes<HTMLButtonElement>}
            onClick={handleButtonClick.bind(null, restProps)}
            // eslint-disable-next-line react/button-has-type
            type={type as buttonType}
        >
            {children}
        </button>
    );
};

/**
 * Base Button
 *
 * To be used as a base for other button components.
 * Renders as a \<button> by default.
 * If an href prop is passed in, component will render as an \<a> instead
 */
const BaseButton = (props: BaseButtonProps) => {
    const { href, ...restProps } = props;
    return href
        ? <Anchor {...props as React.HTMLProps<HTMLAnchorElement>} />
        : <Button {...restProps as React.HTMLProps<HTMLButtonElement>} />;
};

export { BaseButton as default };
export type { BaseButtonProps };
